import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { consoleApi, type AuditEntry } from '@/lib/consoleApi';
import { AdminPage, RegistryTable, Pill } from '@/admin';

type Row = AuditEntry & { _i: number };

const SOURCE_HEX: Record<string, string> = { signoff: '#2BBAA8', audit: '#5B72E8' };

export function AuditPage() {
  const [registry, setRegistry] = useState('');
  const [reviewer, setReviewer] = useState('');
  const q = useQuery({ queryKey: ['audit', registry, reviewer], queryFn: () => consoleApi.audit({ registry: registry || undefined, reviewer: reviewer || undefined }) });

  const rows: Row[] = (q.data ?? []).map((e, i) => ({ ...e, _i: i }));

  return (
    <AdminPage
      title="Audit trail"
      description="Every clinical sign-off and registry change, newest first. Filter by registry or reviewer."
    >
      <div className="mb-13 flex flex-wrap items-center gap-8">
        <input
          value={registry} onChange={(e) => setRegistry(e.target.value)} placeholder="Filter by registry…"
          className="w-[220px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
        <input
          value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="Filter by reviewer…"
          className="w-[220px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
      </div>

      <RegistryTable<Row>
        columns={[
          { key: 'event', header: 'Event', className: 'font-medium', render: (e) => e.decision ?? e.action ?? e._source ?? '—' },
          { key: 'registry', header: 'Registry', className: 'font-mono text-[12px] text-text-secondary', render: (e) => (e.registry as string) || '—' },
          { key: 'reviewer', header: 'Reviewer', className: 'text-[12px] text-text-secondary', render: (e) => (e.reviewer as string) || '—' },
          { key: 'source', header: 'Source', render: (e) => e._source ? <Pill label={e._source} color={SOURCE_HEX[e._source]} /> : <span className="text-text-muted">—</span> },
          { key: 'when', header: 'When', className: 'text-[12px] text-text-secondary whitespace-nowrap', render: (e) => (e.timestamp as string) || '—' },
        ]}
        rows={rows}
        getRowKey={(e) => `${e._source ?? 'x'}-${e.timestamp ?? ''}-${e._i}`}
        count={rows.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={registry || reviewer ? 'No audit entries match these filters.' : 'No audit entries yet.'}
      />
    </AdminPage>
  );
}
